import { Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";

import { AccountService } from "@bitwarden/common/auth/abstractions/account.service";

const STORAGE_KEY_PREFIX = "bv-favorite-sends";

@Injectable({ providedIn: "root" })
export class BvSendFavoriteService {
  constructor(private readonly accountService: AccountService) {}

  async isFavorite(sendId: string): Promise<boolean> {
    const favorites = await this.getFavorites();
    return favorites.has(sendId);
  }

  async setFavorite(sendId: string, favorite: boolean): Promise<void> {
    const favorites = await this.getFavorites();
    if (favorite) {
      favorites.add(sendId);
    } else {
      favorites.delete(sendId);
    }
    await this.saveFavorites(favorites);
  }

  async toggleFavorite(sendId: string): Promise<boolean> {
    const favorites = await this.getFavorites();
    const isFavorite = favorites.has(sendId);
    if (isFavorite) {
      favorites.delete(sendId);
    } else {
      favorites.add(sendId);
    }
    await this.saveFavorites(favorites);
    return !isFavorite;
  }

  private async getStorageKey(): Promise<string | null> {
    const account = await firstValueFrom(this.accountService.activeAccount$);
    if (!account?.id) {
      return null;
    }
    return `${STORAGE_KEY_PREFIX}-${account.id}`;
  }

  private async getFavorites(): Promise<Set<string>> {
    const storageKey = await this.getStorageKey();
    if (!storageKey) {
      return new Set();
    }
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        return new Set();
      }
      const parsed = JSON.parse(raw) as string[];
      return new Set(Array.isArray(parsed) ? parsed : []);
    } catch {
      return new Set();
    }
  }

  private async saveFavorites(favorites: Set<string>): Promise<void> {
    const storageKey = await this.getStorageKey();
    if (!storageKey) {
      return;
    }
    localStorage.setItem(storageKey, JSON.stringify([...favorites]));
  }
}
